import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { getPasswordStrength } from '../../lib/validation';

/**
 * Visual password strength hint for the sign-up form (BG_62). Renders a
 * three-segment meter plus a Weak / Medium / Strong label that updates as the
 * user types. Hidden entirely until the field has a value so it doesn't add
 * noise to an empty form. Purely advisory - it never blocks submission; the
 * length rule in the form remains the only hard requirement.
 */
const LEVELS = {
  weak:   { bar: 'bg-red-500',   text: 'text-red-400',   labelKey: 'auth.password_strength_weak' },
  medium: { bar: 'bg-amber-500', text: 'text-amber-400', labelKey: 'auth.password_strength_medium' },
  strong: { bar: 'bg-green-500', text: 'text-green-400', labelKey: 'auth.password_strength_strong' },
};

const PasswordStrengthMeter = ({ password }) => {
  const { t } = useTranslation();
  const { score, level } = getPasswordStrength(password);

  if (!level) return null;
  const cfg = LEVELS[level];

  return (
    <div className="mt-2" aria-live="polite">
      <div className="flex items-center gap-1.5" data-testid="password-strength-meter">
        {[1, 2, 3].map((segment) => (
          <div
            key={segment}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              segment <= score ? cfg.bar : 'bg-gray-700'
            )}
          />
        ))}
        <span className={cn('ml-1 text-xs font-medium', cfg.text)}>
          {t(cfg.labelKey)}
        </span>
      </div>
    </div>
  );
};

export { PasswordStrengthMeter };
